import type { CartLine } from '@hunk/shared/api/orders';
import { effectivePrice, type ShopProduct } from '@hunk/shared/api/shop';
import { createContext, type ReactNode, useCallback, useContext, useEffect, useMemo, useState } from 'react';

import { useShop } from '@/hooks/useShop';

export interface CartItem {
  product: ShopProduct;
  qty: number;
}

interface CartContextType {
  /** lines whose product is still on sale, with the current product data */
  items: CartItem[];
  count: number;
  /** agorot */
  itemsTotal: number;
  qtyOf: (productId: number) => number;
  add: (productId: number, qty?: number) => void;
  setQty: (productId: number, qty: number) => void;
  remove: (productId: number) => void;
  clear: () => void;
  /** for the checkout request */
  lines: CartLine[];
  /** "added to cart" toast */
  lastAdded: { productId: number; at: number } | undefined;
}

const CartContext = createContext<CartContextType | undefined>(undefined);
const KEY = 'hunk.cart';

const load = (): CartLine[] => {
  try {
    const v = JSON.parse(localStorage.getItem(KEY) ?? '[]') as CartLine[];
    return Array.isArray(v) ? v.filter((l) => Number.isInteger(l?.productId) && Number.isInteger(l?.qty) && l.qty > 0) : [];
  } catch {
    return [];
  }
};

/** the cart lives in the browser (localStorage) - no login needed until checkout */
export function CartProvider({ children }: { children: ReactNode }) {
  const { byId, loading } = useShop();
  const [lines, setLines] = useState<CartLine[]>(load);
  const [lastAdded, setLastAdded] = useState<{ productId: number; at: number }>();

  useEffect(() => {
    try {
      localStorage.setItem(KEY, JSON.stringify(lines));
    } catch {
      // private mode
    }
  }, [lines]);

  // other tabs
  useEffect(() => {
    const onStorage = (e: StorageEvent) => e.key === KEY && setLines(load());
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  const items = useMemo(
    () =>
      lines
        .map((l) => ({ product: byId.get(l.productId), qty: l.qty }))
        .filter((i): i is CartItem => i.product != null && i.product.inStock)
        .map((i) => ({ ...i, qty: Math.min(i.qty, i.product.maxQty) })),
    [lines, byId]
  );

  // products that left the shop drop out of the cart (once the catalog is loaded)
  useEffect(() => {
    if (!loading && byId.size) setLines((ls) => (ls.every((l) => byId.has(l.productId)) ? ls : ls.filter((l) => byId.has(l.productId))));
  }, [loading, byId]);

  const setQty = useCallback(
    (productId: number, qty: number) =>
      setLines((ls) => {
        const max = byId.get(productId)?.maxQty ?? 20;
        const q = Math.max(0, Math.min(Math.floor(qty), max));
        return q === 0
          ? ls.filter((l) => l.productId !== productId)
          : ls.some((l) => l.productId === productId)
            ? ls.map((l) => (l.productId === productId ? { ...l, qty: q } : l))
            : [...ls, { productId, qty: q }];
      }),
    [byId]
  );
  const qtyOf = useCallback((productId: number) => lines.find((l) => l.productId === productId)?.qty ?? 0, [lines]);
  const add = useCallback(
    (productId: number, qty = 1) => {
      setQty(productId, qtyOf(productId) + qty);
      setLastAdded({ productId, at: Date.now() });
    },
    [setQty, qtyOf]
  );
  const remove = useCallback((productId: number) => setLines((ls) => ls.filter((l) => l.productId !== productId)), []);
  const clear = useCallback(() => setLines([]), []);

  const value = useMemo(() => {
    const count = items.reduce((s, i) => s + i.qty, 0);
    const itemsTotal = items.reduce((s, i) => s + effectivePrice(i.product) * i.qty, 0);
    return { items, count, itemsTotal, qtyOf, add, setQty, remove, clear, lines: items.map((i) => ({ productId: i.product.id, qty: i.qty })), lastAdded };
  }, [items, qtyOf, add, setQty, remove, clear, lastAdded]);
  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
}

export function useCart(): CartContextType {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error('useCart must be used inside CartProvider');
  return ctx;
}
